import { useState, useRef } from "react";
import { UploadCloud, Loader2, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl, apiError } from "@/lib/api";

export default function MediaUpload({ label, hint, kind = "image", value, onChange, testId }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef();

  const handle = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/upload?kind=${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success(`${label} uploaded`);
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(false);
  };

  return (
    <div>
      <label className="text-sm font-semibold">{label}</label>
      {hint && <p className="text-xs text-muted-foreground mb-1.5">{hint}</p>}
      <div
        data-testid={testId}
        onClick={() => !busy && inputRef.current?.click()}
        className="relative mt-1 border-2 border-dashed rounded-xl aspect-video flex items-center justify-center overflow-hidden cursor-pointer hover:border-primary transition-colors bg-secondary/40"
      >
        {value ? (
          kind === "video" ? (
            <video src={fileUrl(value)} className="h-full w-full object-cover" muted playsInline />
          ) : (
            <img src={fileUrl(value)} alt={label} className="h-full w-full object-cover" />
          )
        ) : (
          <div className="text-center text-muted-foreground p-4">
            <UploadCloud className="h-7 w-7 mx-auto mb-1" />
            <p className="text-xs font-medium">Click to upload {kind}</p>
          </div>
        )}
        {busy && (
          <div className="absolute inset-0 bg-black/50 grid place-items-center">
            <Loader2 className="h-6 w-6 text-white animate-spin" />
          </div>
        )}
        {value && !busy && (
          <>
            <div className="absolute top-2 left-2"><CheckCircle2 className="h-5 w-5 text-emerald-400" /></div>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onChange(""); }}
              className="absolute top-2 right-2 bg-black/60 rounded-full p-1"
            >
              <X className="h-4 w-4 text-white" />
            </button>
          </>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={kind === "video" ? "video/mp4,video/quicktime,video/webm" : "image/jpeg,image/png,image/webp"}
        className="hidden"
        onChange={(e) => handle(e.target.files?.[0])}
      />
    </div>
  );
}
